/**
 * Tier 1 of the art precache: pull the chassis art for *this table's own mechs* to the head of the
 * sweep, ahead of the other thirty-odd chassis nobody here flies.
 *
 * Mounted by both desktops (`routes/home/index.tsx`), which are where the live roster first exists —
 * boot can't do this itself, since `main.tsx` runs before anyone has logged in and the roster is a
 * websocket away. That ordering is exactly why {@link prioritizeFrameArt} re-orders already-queued
 * URLs instead of dropping them as duplicates (see `assetPrecache.ts`).
 *
 * The live encounter's combatants need no separate tier: a deployed combatant's portrait is
 * resolved from the same roster mech it was deployed from (`lib/encounter/deploy.ts`'s rows feed
 * `resolveMechArtUrl`), so warming the roster covers the encounter surfaces too.
 */
import { useEffect } from "react";
import type { RosterState } from "@lancer-vtt/domain";
import { prioritizeArtUrls, prioritizeFrameArt } from "./assetPrecache";
import { chassisTokenUrls } from "./tokenArt";

/** Every distinct frame id flown by a mech on this roster, in roster order. */
export function rosterFrameIds(roster: RosterState | null): string[] {
  if (roster === null) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const pilot of Object.values(roster.pilots)) {
    for (const mech of pilot.mechs) {
      const id = mech.frameId;
      if (typeof id !== "string" || id === "" || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Every uploaded portrait URL this roster names — pilots and their mechs.
 *
 * **No server endpoint is involved, and none is needed.** An upload's served URL is persisted
 * onto the entity at upload time (`set_pilot_text` field `image`, `set_mech_image`), so the
 * roster the desktops already hold names every one.
 *
 * Empty string is "unset" throughout the roster (see `PilotData.image`'s own doc), so it is
 * skipped rather than warmed as `/`.
 */
export function rosterUploadUrls(roster: RosterState | null): string[] {
  if (roster === null) return [];
  const urls: string[] = [];
  const seen = new Set<string>();
  const push = (url: string | undefined) => {
    if (typeof url !== "string" || url === "" || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  };
  for (const pilot of Object.values(roster.pilots)) {
    push(pilot.image);
    for (const mech of pilot.mechs) push(mech.image);
  }
  return urls;
}

/**
 * Prioritize this roster's own art — its uploaded portraits first, then its chassis board tokens
 * (`/tokens/`, what the combat board draws), then its chassis frames, then (already queued at boot)
 * the rest of the vendored corpus.
 *
 * Re-runs when the roster's art set changes (a mech gaining a frame, a portrait uploaded, a pilot
 * arriving) — the joined keys are the dependency rather than the state object, which changes
 * identity on every unrelated roster command.
 *
 * **Order matters and is the reverse of what it looks like.** Each call unshifts onto the head, so
 * the LAST call ends up first: frames, then tokens, then uploads, so the uploads land at the head.
 */
export function useRosterArtPrecache(roster: RosterState | null): void {
  const frameKey = rosterFrameIds(roster).join(",");
  const uploadKey = rosterUploadUrls(roster).join("\n");
  useEffect(() => {
    if (frameKey !== "") {
      prioritizeFrameArt(frameKey.split(","));
      prioritizeArtUrls(chassisTokenUrls(frameKey.split(",")));
    }
    if (uploadKey !== "") prioritizeArtUrls(uploadKey.split("\n"));
  }, [frameKey, uploadKey]);
}
